import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "motion/react";
import { ArrowRight, ArrowUpRight } from "@phosphor-icons/react";
import { MaskWords, PageFrame, Photo, Seo } from "../../kit.jsx";
import { AAROH_IMAGES } from "../../data/images.js";
import { opportunityPaths } from "../../data/content.js";
import { RevealItem, RollButton, Rise, mailto } from "./shared.jsx";
import "./join.css";

const TRIPTYCH = [
  {
    word: "A problem",
    text: "A real need students can investigate, with someone who can explain the context.",
    image: AAROH_IMAGES.studentDiscussion,
    alt: "Students talking through an idea at AAROH",
    caption: "AAROH, IIIT Delhi",
  },
  {
    word: "A place",
    text: "A room, a venue or equipment that lets students build, test and present.",
    image: AAROH_IMAGES.room,
    alt: "Students working in teams across the AAROH auditorium at IIIT Delhi",
    caption: "AAROH, hosted at IIIT Delhi",
  },
  {
    word: "A pathway",
    text: "A clear next step for young people after one event, or wider access to the ones that exist.",
    image: AAROH_IMAGES.recognition,
    alt: "A student team receiving recognition on stage at AAROH",
    caption: "AAROH. Thirteen teams were recognised",
  },
];

function Panel({ item, index }) {
  const reduce = useReducedMotion();
  return (
    <li className="jn-tri__item">
      <motion.div
        className="jn-tri__img"
        initial={reduce ? false : { clipPath: "inset(0% 46% 0% 46% round 28px)" }}
        whileInView={{ clipPath: "inset(0% 0% 0% 0% round 28px)" }}
        viewport={{ once: true, amount: 0.35 }}
        transition={{ duration: 1.2, delay: index * 0.12, ease: [0.65, 0, 0.35, 1] }}
      >
        <Photo src={item.image} alt={item.alt} sizes="(max-width: 899px) 100vw, 33vw" />
        <span className="jn-tri__cap">{item.caption}</span>
      </motion.div>
      <h3>{item.word}</h3>
      <p>{item.text}</p>
    </li>
  );
}

export default function OrganisationsPage() {
  const selected = opportunityPaths.find((item) => item.slug === "organisations");
  const email = mailto(selected.emailSubject);
  return (
    <PageFrame className="rd-page jn-page jn-org">
      <Seo title={`${selected.title} | Project GRID`} path={`/student-opportunities/${selected.slug}`} description={selected.body} />
      <main>
        <header className="jn-wrap jn-org-hero">
          <Rise as="p" className="jn-mono" delay={0.05}>For organisations</Rise>
          <MaskWords as="h1" className="jn-h1" text="Bring a problem, place or pathway." onMount delay={0.1} />
          <Rise className="jn-org-hero__row" delay={0.4}>
            <p className="jn-lede">{selected.body} Each one gives young builders a real context to work in.</p>
            <RollButton href={email} label="Start an organisation conversation" />
          </Rise>
        </header>

        <section className="jn-wrap jn-tri" aria-label="What an organisation can bring">
          <ul>
            {TRIPTYCH.map((item, index) => <Panel key={item.word} item={item} index={index} />)}
          </ul>
        </section>

        <section className="jn-wrap jn-org-fit" aria-labelledby="jn-org-fit-title">
          <MaskWords id="jn-org-fit-title" className="jn-h2" text="Who this suits." />
          <ul>
            {selected.fits.map((fit, index) => (
              <RevealItem key={fit} delay={index * 0.06}>{fit}</RevealItem>
            ))}
          </ul>
        </section>

        <section className="jn-wrap jn-org-how" aria-labelledby="jn-org-how-title">
          <div className="jn-org-how__head">
            <MaskWords id="jn-org-how-title" className="jn-h2" text="What happens after you write." />
            <p className="jn-lede">Give young builders context they could not create alone. The role is agreed first and published accurately after.</p>
          </div>
          <ol>
            {selected.steps.map((step, index) => (
              <RevealItem key={step} delay={index * 0.07}><span>{String(index + 1).padStart(2, "0")}</span>{step}</RevealItem>
            ))}
          </ol>
        </section>

        <section className="jn-wrap jn-org-end" aria-label="Apply by email">
          <div>
            <h2 className="jn-h3">Send a concise brief.</h2>
            <p>Describe your organisation, the students or community you work with and the specific support you can offer or need. The email opens with the subject "{selected.emailSubject}".</p>
          </div>
          <div className="gh-actions jn-org-end__actions">
            <a className="gh-btn gh-btn--fill" href={email}>Email your brief <ArrowUpRight weight="bold" aria-hidden="true" /></a>
            <Link className="gh-link" to="/for-partners">Full partner details <ArrowRight weight="bold" aria-hidden="true" /></Link>
          </div>
        </section>
      </main>
    </PageFrame>
  );
}

